import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';
import { BookingItem } from 'hooks/useBookingItems';
import { useCallback, useState } from 'react';
import type { BookingHost } from 'types/booking';

interface Venue {
  id: number;
  name: string;
}

interface ClientService {
  id: number;
  name: string;
  disabled: boolean;
}

// The attributes a booking can be patched with. A purchase-order-only update
// goes to its own endpoint, hence the special case in `update` below.
export interface BookingUpdate {
  purchaseOrder?: string;
  bookingHostId?: number;
}

export interface Booking {
  id: number;
  startTime: string;
  endTime: string;
  purchaseOrder: string;
  clientService: ClientService;
  venue: Venue;
  bookingItems: BookingItem[];
  bookingHost: BookingHost | null;
  description?: string;
}

const useBooking = (id?: number, eagerLoad: boolean = true) => {
  const [booking, setBooking] = useState<Booking | undefined>(undefined);

  const apiRequest = useApiRequest();

  const requestValid = Number.isInteger(id) && id! > 0 && eagerLoad;

  const clearData = useCallback(async () => {
    setBooking(undefined);
    return undefined;
  }, []);

  const fetchData = useCallback(async () => {
    const { data: nextBooking } = (await apiRequest.get(
      `/v1/bookings/${id}`
    )) as unknown as { data: Booking };

    setBooking(nextBooking);
    return nextBooking;
  }, [apiRequest, id]);

  const updatePurchaseOrder = useCallback(
    async (purchaseOrder: string) => {
      const { data: booking } = await apiRequest.put(
        `/v1/bookings/${id}/purchase_order`,
        {
          purchaseOrder,
        }
      );

      setBooking(booking);

      return booking;
    },
    [apiRequest, id]
  );

  const update = useCallback(
    async (attributes: BookingUpdate = {}) => {
      if ('purchaseOrder' in attributes && Object.keys(attributes).length === 1)
        return updatePurchaseOrder(attributes.purchaseOrder!);

      const { data: updatedBooking } = (await apiRequest.put(
        `/v1/bookings/${id}`,
        {
          booking: attributes,
        }
      )) as unknown as { data: Booking };

      setBooking(updatedBooking);

      return updatedBooking;
    },
    [apiRequest, updatePurchaseOrder, id]
  );

  const { loading, error, refresh } = useAsync<Booking | undefined>(
    requestValid ? fetchData : clearData,
    requestValid && eagerLoad
  );

  return {
    data: booking,
    loading,
    error,
    refresh,
    update,
  };
};

export default useBooking;
